import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { forkJoin, Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { PermissionGroup } from '../../shared-components/security/permission-group.model';
import { SharedService } from '../../shared-components/shared.service';
import { System } from '../../shared-components/System';
import { UserService } from '../../shared-components/user/user.service';
import { YearService } from '../../shared-components/year/year.service';
import { LoginService } from '../../shared-modules/login/login.service';
import { MyNotificationGroupOverride } from './models/my-notification-group-override.model';
import { MyNotification } from './models/my-notification.model';
import { NotificationArea } from './models/notification-area.model';
import { NotificationEventPermissionCategory } from './models/notification-event-permission-category.model';
import { NotificationEvent } from './models/notification-event.model';
import { NotificationGroupEvent } from './models/notification-group-event.model';
import { NotificationGroupPermissionGroup } from './models/notification-group-permission-group.model';
import { NotificationGroup } from './models/notification-group.model';
import { NotificationLog } from './models/notification-log.model';
import { NotificationType } from './models/notification-type.model';

@Injectable()
export class NotificationsService {

    // Properties
    checkingNotifications = false;
    checkInterval: any;

    // Class Functions
    constructor(
        private http: HttpClient,
        private yearService: YearService,
        private userService: UserService,
        private sharedService: SharedService,
        private loginService: LoginService
    ) { }

    // Event Functions
    public setNotificationChecking(
        notificationTypes: NotificationType[] = null,
        myNotifications: MyNotification[] = null,
        myNotificationGroups: NotificationGroup[] = null
    ) {
        // PRE: optionally, the notification types, notifications, and notification groups the user has
        // POST: stops or starts checking if the user should

        // Checking if we are already checking notifications
        if (this.loginService.LoginSettings.ActivateNotificationFramework360) {

            // We need to make sure we even have in-app notifications first
            if (!notificationTypes || !myNotifications || !myNotificationGroups) {
                forkJoin(
                    this.getMyNotifications(),
                    this.getMyNotificationGroups(),
                    this.getNotificationTypes()
                ).pipe(take(1)).subscribe(results => {
                    if (this.checkForInAppType(results[2], results[0], results[1])) {
                        this.beginCheckingNotifications();
                    } else {
                        this.stopCheckingNotifications();
                    }
                });
            } else {
                if (this.checkForInAppType(notificationTypes, myNotifications, myNotificationGroups)) {
                    this.beginCheckingNotifications();
                } else {
                    this.stopCheckingNotifications();
                }
            }
        }
    }

    // Methods
    private beginCheckingNotifications() {
        // POST: waits for notifications and displays them as snackbars
        if (!this.checkingNotifications) {

            // Setting that we are now checking notifications
            this.checkingNotifications = true;

            // Helper function to perform the check
            const check = () => {
                this.checkNotifications().pipe(take(1)).subscribe(
                    notifications => {
                        if (notifications) {

                            // Showing snackbars for the notifications
                            notifications.forEach(notification => {
                                if (notification.TypeName.toLowerCase() == 'on screen notification') {
                                    this.sharedService.showCustomSnackbar(
                                        (notification.Subject + ': ' + notification.Message).slice(0, 200),
                                        'notification'
                                    );
                                }
                            });
                        }
                    }
                );
            };

            // Checking the notifications upon initialization
            check();

            // Setting the interval for checking notifications
            this.checkInterval = setInterval(() => {
                check();
            }, 60000);
        }
    }

    private stopCheckingNotifications() {
        if (this.checkingNotifications) {
            clearInterval(this.checkInterval);
            this.checkInterval = null;
            this.checkingNotifications = false;
        }
    }

    private checkForInAppType(notificationTypes: NotificationType[], myNotifications: MyNotification[], myNotificationGroups: NotificationGroup[]) {
        // PRE: the notification types, the users notifications, and the users groups
        // POST: returns true if the user has a notification that should who up in the app
        if (notificationTypes) {
            const inAppType = notificationTypes.find(type => type.Name.toLowerCase() == 'on screen notification');
            return inAppType
                && (
                    (
                        myNotifications
                        && myNotifications.find(notificationSetting => notificationSetting.NotificationType_ID == inAppType.ID)
                    ) || (
                        myNotificationGroups
                        && myNotificationGroups.find(notificationGroup =>
                            !!notificationGroup.NotificationGroupEvents.find(event =>
                                event.NotificationType_ID == inAppType.ID
                                && !notificationGroup.MyNotificationGroupOverrides.find(override =>
                                    override.NotificationEvent_ID == event.NotificationEvent_ID
                                    && override.NotificationType_ID == inAppType.ID
                                    && !override.OverrideValue
                                )
                            ) || notificationGroup.MyNotificationGroupOverrides.find(override =>
                                override.NotificationType_ID == inAppType.ID
                                && override.OverrideValue
                            )
                        )
                    )
                );
        }
    }

    public getNotificationTypes(): Observable<NotificationType[]> {
        return this.http.get<NotificationType[]>(System.apiURL.Admin + 'GetNotificationTypes');
    }

    public getNotificationAreasAndEvents(): Observable<NotificationArea[]> {
        return this.http.get<NotificationArea[]>(System.apiURL.Admin + 'GetNotificationAreasAndEvents').pipe(
            map(areas => {
                for (let i = 0; i < areas.length; i++) {
                    areas[i] = System.convert(areas[i], new NotificationArea());
                    for (let j = 0; j < areas[i].NotificationEvents.length; j++) {
                        areas[i].NotificationEvents[j] = System.convert(areas[i].NotificationEvents[j], new NotificationEvent());
                        areas[i].NotificationEvents[j].areaName = areas[i].Name;
                        if (areas[i].NotificationEvents[j].PermissionCategory) {
                            areas[i].NotificationEvents[j].PermissionCategory = System.convert(areas[i].NotificationEvents[j].PermissionCategory, new NotificationEventPermissionCategory());
                        }
                    }
                }
                return areas;
            })
        );
    }

    public getNotificationGroups(): Observable<NotificationGroup[]> {
        return this.http.get<NotificationGroup[]>(System.apiURL.Admin + 'GetNotificationGroups').pipe(
            map(groups => {
                for (let i = 0; i < groups.length; i++) {
                    groups[i] = System.convert(groups[i], new NotificationGroup());
                    for (let j = 0; j < groups[i].NotificationGroupEvents.length; j++) {
                        groups[i].NotificationGroupEvents[j] = System.convert(groups[i].NotificationGroupEvents[j], new NotificationGroupEvent());
                    }
                    for (let j = 0; j < groups[i].NotificationPermissionGroups.length; j++) {
                        groups[i].NotificationPermissionGroups[j] = System.convert(groups[i].NotificationPermissionGroups[j], new NotificationGroupPermissionGroup());
                    }
                }
                return groups;
            })
        );
    }

    public getMyNotificationGroups(): Observable<NotificationGroup[]> {
        return this.http.get<NotificationGroup[]>(System.apiURL.Admin + 'GetMyNotificationGroups').pipe(
            map(groups => {
                if (groups) {
                    for (let i = 0; i < groups.length; i++) {
                        groups[i] = System.convert(groups[i], new NotificationGroup());
                        for (let j = 0; j < groups[i].NotificationGroupEvents.length; j++) {
                            groups[i].NotificationGroupEvents[j] = System.convert(groups[i].NotificationGroupEvents[j], new NotificationGroupEvent());
                        }
                        for (let j = 0; j < groups[i].NotificationPermissionGroups.length; j++) {
                            groups[i].NotificationPermissionGroups[j] = System.convert(groups[i].NotificationPermissionGroups[j], new NotificationGroupPermissionGroup());
                            groups[i].NotificationPermissionGroups[j].PermissionGroup = System.convert(groups[i].NotificationPermissionGroups[j].PermissionGroup, new PermissionGroup());
                        }
                        for (let j = 0; j < groups[i].MyNotificationGroupOverrides.length; j++) {
                            groups[i].MyNotificationGroupOverrides[j] = System.convert(groups[i].MyNotificationGroupOverrides[j], new MyNotificationGroupOverride());
                        }
                    }
                }
                return groups;
            })
        );
    }

    public getMyNotifications(): Observable<MyNotification[]> {
        return this.http.get<MyNotification[]>(System.apiURL.Admin + 'GetMyNotifications').pipe(
            map(myNotifications => {
                for (let i = 0; i < myNotifications.length; i++) {
                    myNotifications[i] = System.convert(myNotifications[i], new MyNotification());
                }
                return myNotifications;
            })
        );
    }

    public checkNotifications(): Observable<NotificationLog[]> {
        return this.http.get<NotificationLog[]>(System.apiURL.Admin + 'CheckNotifications');
    }

    public getMyNotificationLogs(number = 10, uniqueInstances = false): Observable<NotificationLog[]> {
        const queryString = '?number=' + number + '&uniqueInstances=' + uniqueInstances;
        return this.http.get<NotificationLog[]>(System.apiURL.Admin + 'GetMyNotificationLogs' + queryString).pipe(
            map(logs => {
                logs.forEach(log => {
                    log.AddedOn = new Date(log.AddedOn);
                });
                return logs;
            })
        );
    }

    public deleteNotificationGroup(notificationGroupId): Observable<void> {
        const queryString = '?notificationGroupId=' + notificationGroupId;
        return this.http.get<void>(System.apiURL.Admin + 'DeleteNotificationGroup' + queryString);
    }

    public saveNotificationGroups(notificationGroups: NotificationGroup[]): Observable<{ [previousId: number]: number }> {
        return this.http.post<{ [previousId: number]: number }>(System.apiURL.Admin + 'SaveNotificationGroups', notificationGroups).pipe(
            map(idMap => {
                notificationGroups.forEach(notificationGroup => {
                    notificationGroup.ID = idMap[notificationGroup.ID];
                });
                return idMap;
            })
        );
    }

    public saveNotificationEvents(notificationEvents: NotificationEvent[]): Observable<void> {
        return this.http.post<void>(System.apiURL.Admin + 'SaveNotificationEvents', notificationEvents);
    }

    public saveMyNotifications(myNotifications: MyNotification[]): Observable<{ [previousId: number]: number }> {
        return this.http.post<{ [previousId: number]: number }>(System.apiURL.Admin + 'SaveMyNotifications', myNotifications);
    }

    public saveMyNotificationGroupOverrides(notificationEvent_ID, myNotificationGroupOverrides: MyNotificationGroupOverride[]): Observable<{ [previousId: number]: number }> {
        const queryString = '?notificationEvent_ID=' + notificationEvent_ID;
        return this.http.post<{ [previousId: number]: number }>(
            System.apiURL.Admin + 'SaveMyNotificationGroupOverrides' + queryString,
            myNotificationGroupOverrides
        );
    }
}
